/**
 * The one place the bottom banners live: the unsent-balls notice, the guest notice and the
 * install bar.
 *
 * Each of them used to pin itself to the bottom edge with its own hard-coded offset, which
 * only works while no two of them are ever on screen together. They are — a guest on a phone
 * with unsent balls and an install prompt gets all three — and the install bar then sat on
 * top of the "Discard" button inside the unsent-balls notice. One flex column, children in
 * source order from the bottom up, cannot overlap whatever combination appears.
 *
 * It also reports its height as `--banner-stack-h`, which the page adds to its bottom
 * padding, so the last thing on a screen can always be scrolled clear of the banners.
 */
import { useEffect, useRef } from "react";

const HEIGHT_VAR = "--banner-stack-h";

export function BannerStack({ children }: { readonly children: React.ReactNode }): React.ReactElement {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    // No ResizeObserver (old WebViews, jsdom): the page keeps its normal padding.
    if (element === null || typeof ResizeObserver === "undefined") return;
    const root = document.documentElement;
    const report = (): void => root.style.setProperty(HEIGHT_VAR, `${element.offsetHeight}px`);
    report();
    const observer = new ResizeObserver(report);
    observer.observe(element);
    return () => {
      observer.disconnect();
      root.style.removeProperty(HEIGHT_VAR);
    };
  }, []);

  return (
    <div className="banner-stack" ref={ref}>
      {children}
    </div>
  );
}
